import {
  FieldInfo,
  hasFieldError,
  readOnlyTriggerClass,
  useFieldContext,
  useFormReadOnly,
  useIsFieldOptional,
} from './shared'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectGroup, SelectValue } from '../../ui/select'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

export default function SimpleSelectNumberField(props: {
  label: string
  options: { value: number; label: string }[]
  className?: string
  orientation?: 'vertical' | 'horizontal'
  placeholder?: string
  optional?: boolean
}) {
  const field = useFieldContext<number | null>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'

  return (
    <div className={cn(props.className, formReadOnly && readOnlyTriggerClass)}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          <Select
            value={field.state.value?.toString() ?? ''}
            // Radix Select spuriously emits an empty-string change while its controlled `value` transitions from ''
            // to a real one; letting it through nulls the field and marks it touched, blocking the resync.
            onValueChange={(val) => {
              if (val !== '') field.handleChange(Number(val))
            }}
          >
            <SelectTrigger
              id={field.name}
              aria-invalid={hasFieldError(field.state.meta)}
              tabIndex={formReadOnly ? -1 : undefined}
              className="w-full"
            >
              {/* Render the label explicitly. Radix only surfaces the selected item's text via a
                  portal from the mounted item, but the items live in lazily-mounted SelectContent
                  (closed at mount) — so a value set from defaultValues showed an empty trigger until
                  the dropdown was opened once. Passing children makes Radix render them directly. */}
              <SelectValue placeholder={formReadOnly ? '-' : props.placeholder}>
                {props.options.find((option) => option.value === field.state.value)?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {props.options.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
